// ═══════════════════════════════════════════════════════════════
// SHELTER MANAGEMENT — Dashboard
// KPI cards, shelter overview, capacity utilization, recent intake
// ═══════════════════════════════════════════════════════════════

import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  SimpleGrid, Paper, Text, Title, Group, Badge, Stack, Table, Loader, Center, Button,
  Progress,
} from '../../components/gab/GabComponents';
import {
  IconBuildingCommunity, IconUsers, IconUsersGroup, IconPercentage,
} from '@tabler/icons-react';
import { useShelterStore } from '../../store/shelterStore';
import { useAuthStore } from '../../store/authStore';
import { SHELTER_STATUS_COLORS } from '../../types/shelter';
import type { ShelterStatus } from '../../types/shelter';

export default function ShelterDashboard() {
  const navigate = useNavigate();
  const { activeIncident } = useAuthStore();
  const incidentId = activeIncident?.id?.toString() ?? '';

  const {
    shelters, survivors, families,
    sheltersLoading, survivorsLoading,
    loadShelters, loadSurvivors, loadFamilies,
  } = useShelterStore();

  useEffect(() => {
    loadShelters();
    loadSurvivors();
    loadFamilies();
  }, [incidentId, loadShelters, loadSurvivors, loadFamilies]);

  // Store already server-side filters by active incident; use directly
  const iShelters = incidentId ? shelters : [];
  const iSurvivors = incidentId ? survivors : [];
  const iFamilies = incidentId ? families : [];

  // ── Computed stats ─────────────────────────────────────────
  const openShelters = useMemo(() => iShelters.filter(s => s.status === 'open' || s.status === 'active').length, [iShelters]);
  const standbyShelters = useMemo(() => iShelters.filter(s => s.status === 'standby').length, [iShelters]);
  const closedShelters = useMemo(() => iShelters.filter(s => s.status === 'closed').length, [iShelters]);
  const checkedInSurvivors = useMemo(() => iSurvivors.filter(s => s.status === 'checked_in').length, [iSurvivors]);

  const capacityStats = useMemo(() => {
    const totalCapacity = iShelters.reduce((sum, s) => sum + s.capacity, 0);
    const totalPopulation = iShelters.reduce((sum, s) => sum + s.currentPopulation, 0);
    const pct = totalCapacity > 0 ? Math.round((totalPopulation / totalCapacity) * 100) : 0;
    return { totalCapacity, totalPopulation, pct };
  }, [iShelters]);

  // Recent intake — last 10 survivors sorted by intakeTimestamp desc (fallback to id desc)
  const recentIntake = useMemo(() => {
    const sorted = [...iSurvivors].sort((a, b) => {
      const ta = a.intakeTimestamp ? new Date(a.intakeTimestamp).getTime() : 0;
      const tb = b.intakeTimestamp ? new Date(b.intakeTimestamp).getTime() : 0;
      if (ta !== tb) return tb - ta;
      return b.id - a.id; // fallback: newer record_id first
    });
    return sorted.slice(0, 10);
  }, [iSurvivors]);

  const isLoading = sheltersLoading || survivorsLoading;

  if (isLoading && iShelters.length === 0) {
    return <Center h={300}><Loader size="lg" /></Center>;
  }

  return (
    <Stack gap="lg">
      {/* Header */}
      <Group justify="space-between">
        <div>
          <Title order={3} c="black">Shelter</Title>
          <Text size="sm" c="dimmed">
            {activeIncident?.name ? `${activeIncident.name} — ` : ''}{iShelters.length} shelters, {iSurvivors.length} survivors
          </Text>
        </div>
      </Group>

      {/* KPI Cards */}
      <SimpleGrid cols={{ base: 2, sm: 4 }}>
        <KPICard icon={IconBuildingCommunity} label="Total Shelters" value={iShelters.length}
          sub={`${openShelters} open · ${standbyShelters} standby · ${closedShelters} closed`} color="#0284c7" onClick={() => navigate('/shelter/shelters')} />
        <KPICard icon={IconUsers} label="Total Survivors" value={iSurvivors.length}
          sub={`${checkedInSurvivors} checked in`} color="#16a34a" onClick={() => navigate('/shelter/survivors')} />
        <KPICard icon={IconPercentage} label="Capacity Utilization" value={capacityStats.pct}
          sub={`${capacityStats.totalPopulation.toLocaleString()} / ${capacityStats.totalCapacity.toLocaleString()}`} color="#d97706" onClick={() => navigate('/shelter/shelters')} />
        <KPICard icon={IconUsersGroup} label="Families" value={iFamilies.length}
          sub={`${iFamilies.reduce((sum, f) => sum + f.memberCount, 0)} members`} color="#7c3aed" onClick={() => navigate('/shelter/families')} />
      </SimpleGrid>

      {/* Middle Row: Shelters Table + Recent Intake */}
      <SimpleGrid cols={{ base: 1, md: 2 }}>
        {/* Shelters Table */}
        <Paper p="md" radius="md" withBorder>
          <Group justify="space-between" mb="sm">
            <Text fw={700}>Shelters</Text>
            {iShelters.length > 10 && (
              <Button size="compact-xs" variant="subtle" onClick={() => navigate('/shelter/shelters')}>
                View All ({iShelters.length})
              </Button>
            )}
          </Group>
          {iShelters.length === 0 ? (
            <Text c="dimmed" size="sm">No shelters created yet</Text>
          ) : (
            <Table striped highlightOnHover withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th ta="right">Pop / Cap</Table.Th>
                  <Table.Th>Type</Table.Th>
                  <Table.Th>Org</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {iShelters.slice(0, 10).map(s => (
                  <Table.Tr key={s.id} style={{ cursor: 'pointer' }}
                    onClick={() => navigate('/shelter/shelters')}>
                    <Table.Td>
                      <Text size="sm" fw={500}>{s.name}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge size="sm" color={SHELTER_STATUS_COLORS[s.status as ShelterStatus] ?? 'gray'}>
                        {s.status ? s.status.replace(/_/g, ' ') : 'standby'}
                      </Badge>
                    </Table.Td>
                    <Table.Td style={{ minWidth: 120 }}>
                      {(() => {
                        const pct = s.capacity > 0 ? Math.round((s.currentPopulation / s.capacity) * 100) : 0;
                        const color = pct >= 90 ? 'red' : pct >= 70 ? 'orange' : 'green';
                        return (
                          <div>
                            <Group justify="space-between" mb={2}>
                              <Text size="xs" fw={500}>{s.currentPopulation}/{s.capacity}</Text>
                              <Text size="xs" c={pct >= 90 ? 'red' : 'dimmed'}>{pct}%</Text>
                            </Group>
                            <Progress value={pct} color={color} size="sm" />
                          </div>
                        );
                      })()}
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{s.shelterType ? s.shelterType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '—'}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">{s.operatingOrg || '—'}</Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Paper>

        {/* Recent Intake */}
        <Paper p="md" radius="md" withBorder>
          <Group justify="space-between" mb="sm">
            <Text fw={700}>Recent Intake</Text>
            {iSurvivors.length > 10 && (
              <Button size="compact-xs" variant="subtle" onClick={() => navigate('/shelter/survivors')}>
                View All ({iSurvivors.length})
              </Button>
            )}
          </Group>
          {recentIntake.length === 0 ? (
            <Text c="dimmed" size="sm">No survivors registered yet</Text>
          ) : (
            <Table striped highlightOnHover withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Intake</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {recentIntake.map(s => (
                  <Table.Tr key={s.id} style={{ cursor: 'pointer' }}
                    onClick={() => navigate('/shelter/survivors')}>
                    <Table.Td>
                      <Text size="sm" fw={500}>{s.firstName} {s.lastName}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge size="sm" color={
                        s.status === 'checked_in' ? 'green' :
                        s.status === 'transferred' ? 'blue' :
                        s.status === 'discharged' ? 'gray' :
                        s.status === 'missing' ? 'red' : 'gray'
                      }>
                        {s.status ? s.status.replace(/_/g, ' ') : 'checked in'}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">
                        {s.intakeTimestamp ? new Date(s.intakeTimestamp).toLocaleDateString() : '—'}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Paper>
      </SimpleGrid>

      {/* Public Pre-Registration Link */}
      <Paper p="md" radius="md" withBorder>
        <Group justify="space-between">
          <div>
            <Text fw={600} size="sm">Public Pre-Registration</Text>
            <Text size="xs" c="dimmed">Share this link so people can pre-register before arriving at the shelter.</Text>
          </div>
          <Button variant="light" size="xs" onClick={() => {
            const url = `${window.location.origin}/shelter/pre-register`;
            navigator.clipboard.writeText(url);
            alert('Pre-registration link copied: ' + url);
          }}>
            Copy Link
          </Button>
        </Group>
      </Paper>
    </Stack>
  );
}

// ── KPI Card Component ──────────────────────────────────────

function KPICard({ label, value, sub, color, onClick }: {
  icon?: React.ComponentType<{ size?: number; stroke?: number }>;
  label: string;
  value: number;
  sub: string;
  color: string;
  onClick: () => void;
}) {
  return (
    <Paper p="sm" radius="md" withBorder ta="center"
      style={{ cursor: 'pointer', borderTop: `3px solid ${color}` }}
      onClick={onClick}>
      <Text size="xs" tt="uppercase" fw={700} c="dimmed">{label}</Text>
      <Text fz={28} fw={800} style={{ color }}>{value}{label === 'Capacity Utilization' ? '%' : ''}</Text>
      <Text size="xs" c="dimmed">{sub}</Text>
    </Paper>
  );
}
